import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatPercent = (rate) => {
  const parsed = Number(rate);
  if (Number.isNaN(parsed)) return '—';
  const pct = parsed > 1 ? parsed : parsed * 100;
  return `${pct.toFixed(2)}%`;
};

export const leadBuyerAddress = (lead) => {
  if (!lead) return { city: '', state: '', zip: '' };
  if (lead.type === 'financing') {
    const applicant = lead.details?.applicant || {};
    return { city: applicant.city || '', state: applicant.state || '', zip: applicant.zip || '' };
  }
  if (lead.type === 'trade_in') {
    const personal = lead.details?.personalInfo || {};
    return { city: personal.city || '', state: personal.state || '', zip: personal.zip || '' };
  }
  return {
    city: lead.details?.city || lead.city || '',
    state: lead.details?.state || lead.state || '',
    zip: lead.details?.zip || lead.zip || '',
  };
};

export function useSalesTaxLookup(token, { state, zip, city }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);

  useEffect(() => {
    const normState = (state || '').trim();
    const normZip = String(zip || '').replace(/\D/g, '');
    if (normState.length < 2 || normZip.length < 5) {
      setResult(null);
      setError('');
      return undefined;
    }

    const timer = setTimeout(async () => {
      const id = ++requestId.current;
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/admin/tax-rates/lookup`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            state: normState,
            zip_code: normZip,
            ...(city ? { city } : {}),
          },
        });
        if (id === requestId.current) setResult(data);
      } catch (err) {
        if (id === requestId.current) {
          setResult(null);
          setError(err.response?.data?.detail || 'Tax lookup failed');
        }
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [token, state, zip, city]);

  return { result, loading, error };
}

export default function SalesTaxRatePanel({
  token,
  state,
  zip,
  city,
  salesTaxPercent,
  onRateChange,
  override,
  onOverrideChange,
  overrideReason,
  onOverrideReasonChange,
  testIdPrefix = 'tax',
  autoApply = true,
  rowClassName = "text-sm font-['Manrope']",
  labelClassName = 'text-white/50',
  overrideLabelClassName = "text-xs font-['Manrope'] text-white/60",
}) {
  const { result, loading, error } = useSalesTaxLookup(token, { state, zip, city });
  const appliedKey = useRef('');

  useEffect(() => {
    if (!result || override || !autoApply) return;
    const key = `${result.state}|${result.zip_code}|${result.estimated_combined_rate}|${result.source}`;
    if (appliedKey.current === key) return;
    appliedKey.current = key;
    const percent = (Number(result.estimated_combined_rate) * 100).toFixed(2);
    if (String(salesTaxPercent) !== percent) onRateChange?.(percent);
  }, [result, override, autoApply, onRateChange, salesTaxPercent]);

  const region = result?.tax_region_name || city || '';
  const showWarning = Boolean(error || result?.source === 'fallback' || (result && result.risk_level > 0));

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-sales-tax-panel`}>
      <div className={`flex justify-between items-start gap-3 ${rowClassName}`}>
        <span className={`${labelClassName} pt-1.5`}>Sales Tax %</span>
        <input
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={salesTaxPercent}
          onChange={(e) => onRateChange?.(e.target.value)}
          className="w-28 bg-white/5 border border-white/10 text-white text-sm px-2 py-1.5 font-['Manrope'] text-right tabular-nums focus:outline-none focus:border-blue-500/50"
          data-testid={`${testIdPrefix}-sales-tax-rate`}
        />
      </div>

      {loading && (
        <p className="text-[11px] font-['Manrope'] text-white/35">Looking up rate for buyer ZIP…</p>
      )}

      {result && (
        <div className="text-[11px] font-['Manrope'] text-white/55 leading-relaxed" data-testid={`${testIdPrefix}-tax-preview`}>
          <p>
            Sales Tax: {formatPercent(result.estimated_combined_rate)}
            {region ? ` (${region}${result.zip_code ? ` – ZIP ${result.zip_code}` : ''})` : result.zip_code ? ` (ZIP ${result.zip_code})` : ''}
          </p>
          <p>
            State {formatPercent(result.state_rate)} + County {formatPercent(result.estimated_county_rate)} + City {formatPercent(result.estimated_city_rate)}
          </p>
        </div>
      )}

      {showWarning && (
        <p className="text-[11px] font-['Manrope'] text-amber-400" data-testid={`${testIdPrefix}-tax-warning`}>
          {error
            ? error
            : result?.source === 'fallback'
              ? 'Using fallback 8.50% — no rate found for this address.'
              : `Rate risk level ${result?.risk_level}. Confirm before closing.`}
        </p>
      )}

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={Boolean(override)}
          onChange={(e) => onOverrideChange?.(e.target.checked)}
          className="accent-blue-500 mt-0.5"
          data-testid={`${testIdPrefix}-tax-override`}
        />
        <span className={overrideLabelClassName}>Manual tax-rate override</span>
      </label>
      {override && (
        <textarea
          value={overrideReason || ''}
          onChange={(e) => onOverrideReasonChange?.(e.target.value)}
          placeholder="Required: reason for override"
          data-testid={`${testIdPrefix}-tax-override-reason`}
          className="w-full min-h-[64px] bg-white/5 border border-white/10 text-white text-sm px-3 py-2 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
        />
      )}
    </div>
  );
}
